import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MapPin, Truck, Percent, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import PageHero from '../components/PageHero';
import DocumentsRequired from '../components/DocumentsRequired';
import ProcessSteps from '../components/ProcessSteps';
import GuaranteeCard from '../components/GuaranteeCard';
import Reviews from '../components/Reviews';
import { ContactSection, Footer } from '../components/Footer';
import { getState } from '../data/states';

const setMeta = (state) => {
  document.title = `Virtual Office in ${state.name} for GST Registration | EasyWorks`;
  const desc = `Section 35-compliant VPOB across ${state.name}. GST registration in 7 working days. Amazon FBA & Flipkart FBF ready. Starting ₹${state.price}/month.`;
  let m = document.querySelector('meta[name="description"]');
  if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
  m.setAttribute('content', desc);
};

const WhyStateVPOB = ({ state }) => (
  <section className="bg-white py-14 md:py-16">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Why {state.name}</div>
        <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 leading-[1.15]">
          Why top e-commerce sellers choose{' '}
          <span className="text-[#F39200]">{state.name} VPOB</span>
        </h2>
      </div>
      <div className="mt-8 grid md:grid-cols-3 gap-5">
        <div className="bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-6">
          <div className="w-11 h-11 rounded-xl bg-white shadow-sm text-[#F39200] flex items-center justify-center">
            <Truck size={20} />
          </div>
          <div className="mt-4 text-lg font-semibold text-neutral-900">Access {state.name}&apos;s FCs</div>
          <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
            Store inventory in {state.fcs} fulfilment centres inside {state.name}. Unlock the Amazon Prime / Flipkart F-Assured badge and win the Buy Box.
          </p>
        </div>
        <div className="bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-6">
          <div className="w-11 h-11 rounded-xl bg-white shadow-sm text-[#F39200] flex items-center justify-center">
            <Percent size={20} />
          </div>
          <div className="mt-4 text-lg font-semibold text-neutral-900">Save 50% on shipping</div>
          <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
            Convert expensive IGST inter-state shipments into local CGST + SGST deliveries within {state.name}. Cut logistics cost by up to half.
          </p>
        </div>
        <div className="bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-6">
          <div className="w-11 h-11 rounded-xl bg-white shadow-sm text-[#F39200] flex items-center justify-center">
            <Zap size={20} />
          </div>
          <div className="mt-4 text-lg font-semibold text-neutral-900">Same-day delivery</div>
          <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
            Serve customers across {state.name} in 24 hours. Faster delivery drives higher conversion and better product ranking.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PriceBlock = ({ state }) => (
  <section className="bg-white pb-2">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">{state.name} VPOB · Starting at</div>
          <div className="mt-1 text-3xl md:text-4xl font-extrabold text-neutral-900">₹{state.price}<span className="text-base font-medium text-neutral-500">/month</span></div>
          <div className="mt-1 text-sm text-neutral-600">Notarised Rent Agreement + NOC · Utility bill · GST filing + APOB · Lifetime renewal support</div>
        </div>
        <a href="#state-quote" className="inline-flex items-center gap-2 bg-[#F39200] hover:bg-[#E08400] text-white h-12 px-6 rounded-lg text-sm font-semibold transition-colors self-start md:self-center">
          Talk to a specialist
        </a>
      </div>
    </div>
  </section>
);

const CoveredCities = ({ state }) => (
  <section className="bg-[#FAF2E8] py-14">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Coverage in {state.name}</div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Cities we serve in {state.name}</h2>
      <div className="mt-6 flex flex-wrap gap-2.5">
        {state.cities.map((c) => (
          <span key={c} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-black/5 rounded-full text-sm text-neutral-800 hover:border-[#F39200] hover:text-[#F39200] transition-colors cursor-default">
            <MapPin size={14} className="text-[#F39200]" /> {c}
          </span>
        ))}
      </div>
      <div className="mt-6">
        <Link to="/vpob-apob" className="text-sm text-[#F39200] hover:text-[#E08400] font-semibold">← See all states</Link>
      </div>
    </div>
  </section>
);

export default function StatePage() {
  const { slug } = useParams();
  const state = getState(slug);
  useEffect(() => { if (state) setMeta(state); }, [state]);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!state) return <Navigate to="/vpob-apob" replace />;
  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid={`state-page-${slug}`}>
      <Navbar />
      <div id="state-quote">
        <PageHero
          eyebrow={`VPOB · ${state.name}`}
          headline="Virtual office in"
          headlineAccent={`${state.name}`}
          description={`Section 35-compliant VPOB across ${state.name}. Get your GSTIN in as little as 7 working days. Amazon FBA, Flipkart FBF and Meesho ready. Starting ₹${state.price}/month.`}
          category="vpob"
          subtype={state.name}
          service="VPOB & APOB"
          formTitle={`Get a quote for ${state.name}`}
          formSubtitle="Custom pricing, KYC checklist and timeline in 24 hours."
          formTestId={`state-quote-form-${slug}`}
        />
      </div>
      <PriceBlock state={state} />
      <WhyStateVPOB state={state} />
      <DocumentsRequired />
      <ProcessSteps />
      <CoveredCities state={state} />
      <GuaranteeCard />
      <Reviews />
      <ContactSection />
      <Footer />
    </div>
  );
}
